import { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { BaseImage } from "@components/BaseImage";
import { Typography, Button } from "@pondigitalsolutions/pon-component-library";
import { PageBody } from "../storyblok/PageBody";
import { Link, Navigate, useParams } from "react-router-dom";
import { formatDate } from "../utils/date";
import { NewsListItem } from "@components/NewsItem/NewsListItem";
import { useNews } from "../hooks/useNews";
import { NewsStory } from "../types/NewsTypes";
import { FallbackLanguageWarning } from "@components/FallbackLanguageWarning";
import { AutoTranslatedWarning } from "@components/AutoTranslatedWarning";
import { Layout } from "@components/Layout";
import { Loader } from "@components/Loader";
import {
  DateFormatEN,
  DateFormatNL,
  relatedNewsItemsCount,
} from "../constants";
import { languageDutch, languageEnglish } from "../i18n";
import { getStoryNameInLocale } from "../utils/getDataInLocale";
import { useTranslation } from "react-i18next";
import { isStoryTranslated } from "../utils/isStoryTranslated";
import { isStoryAutoTranslated } from "../utils/isStoryAutoTranslated";
import { getPageSlugFromPathname } from "../utils/getPageSlugFromPathname";
import { ShareButton } from "@components/ShareButton";
import { useUserContext } from "@hooks/useUserContext";
import { useUpdateArticlesRead } from "@hooks/useUpdateArticlesRead";
import { useGetArticleLikeCount } from "@hooks/useGetArticleLikeCount";
import { useToggleArticleLike } from "@hooks/useToggleArticleLike";
import { usePreservedSearchQuery } from "@hooks/usePreservedSearchQuery";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { useEditorPreviewFallback } from "@hooks/useEditorPreviewFallback";

export const DetailPage = () => {
  const { lang } = useParams();
  const { user } = useUserContext();
  const currentLang = useCurrentLang(lang);

  const { t } = useTranslation();
  const { mutate: updateArticlesRead } = useUpdateArticlesRead();
  const { mutate: toggleLike } = useToggleArticleLike();
  const { preservedSearch, linkState } = usePreservedSearchQuery();

  const pageSlug = getPageSlugFromPathname(window.location.pathname);

  const { data, isLoading, error } = useNews({
    slug: pageSlug,
    resolve_relations: "brand,categories,cluster",
    language: lang === languageEnglish ? languageEnglish : undefined,
    content_type: "NewsArticle",
    autoTranslateLanguage:
      user?.autoTranslateEnabled && user.autoTranslateLanguage
        ? user.autoTranslateLanguage
        : undefined,
  });
  const story: NewsStory | undefined = data?.storyData?.[0];

  const { data: likeData } = useGetArticleLikeCount(story?.id);

  const {
    data: relatedData,
    isLoading: relatedStoriesLoading,
    error: relatedStoriesError,
  } = useNews({
    content_type: "NewsArticle",
    per_page: relatedNewsItemsCount,
    resolve_relations: "cluster",
    sort_by: "content.date:desc",
    excluding_slugs: pageSlug,
    language: lang === languageEnglish ? languageEnglish : undefined,
    filter_query: {
      storyTranslated: {
        is: lang === languageEnglish ? true : null,
      },
    },
  });
  const relatedStories = relatedData?.storyData;

  const hasRelatedStories =
    !relatedStoriesLoading &&
    !relatedStoriesError &&
    relatedStories &&
    relatedStories?.length > 0;

  useEffect(() => {
    if (story?.id) {
      // Mark the article as read after 5 seconds.
      const id = setTimeout(
        () => updateArticlesRead({ articleId: story.id }),
        5000
      );
      return () => clearTimeout(id);
    }
  }, [story?.id, updateArticlesRead]);

  // Inside the Visual Editor, a rejected draft (invalid/expired token) or a missing story
  // would otherwise hard-redirect to /404 — stripping the signed `_storyblok_tk` params and
  // breaking the editor session. Show a clear, in-place preview-unavailable message instead.
  const previewFallback = useEditorPreviewFallback(
    !isLoading && (!!error || !story)
  );
  if (previewFallback) return previewFallback;

  return (
    <Layout
      translatedSlug={
        currentLang === languageDutch
          ? `/${languageEnglish}/${
              story?.translated_slugs?.find(
                (obj) => obj.lang === languageEnglish
              )?.path
            }`
          : `/${story?.default_full_slug}`
      }
      className="mb-24"
      trackingParams={{
        articleCluster: story?.content?.cluster
          ?.map((cluster) => cluster.name)
          .join(", "),
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader />
        </div>
      )}
      {error && <p>{t("NewsDetailPage.errorFetching")}</p>}
      {!isLoading && !story && <Navigate to={lang ? `/${lang}/404` : `/404`} />}

      {story && (
        <>
          <title>
            {getStoryNameInLocale(
              story,
              lang,
              user?.autoTranslateEnabled ?? false
            )}
          </title>

          <article className="mx-auto flex justify-center">
            <div className="my-16 w-full max-w-4xl">
              {story.content.featuredImage && (
                <BaseImage
                  imageUrl={story.content.featuredImage.filename}
                  alt={story.content.featuredImage.alt ?? ""}
                  className="mb-7 w-full object-cover lg:mb-14"
                  attributes={{ "data-testid": "featured-image" }}
                  width={896}
                />
              )}

              <div className="px-7 md:px-0">
                <div className="relative mx-auto mb-6 flex max-w-2xl flex-col">
                  {/* Show appropriate language warning based on auto-translate state. */}
                  {isStoryAutoTranslated({ user, story, lang }) ? (
                    <AutoTranslatedWarning />
                  ) : (
                    !isStoryTranslated(story) &&
                    lang === languageEnglish && <FallbackLanguageWarning />
                  )}
                  <div className="flex items-center justify-between gap-2.5 lg:gap-4">
                    {story.content.date && (
                      <Typography
                        size="sm"
                        className="text-tertiary-500 lg:text-base dark:text-white"
                      >
                        {formatDate(
                          story.content.date,
                          user?.languagePreference === languageEnglish
                            ? DateFormatEN
                            : DateFormatNL
                        )}
                      </Typography>
                    )}
                    <InteractButtons
                      t={t}
                      onLikeClick={() => toggleLike({ articleId: story.id })}
                      liked={likeData?.liked}
                      likeCount={likeData?.likeCount ?? 0}
                      id="like-top"
                    />
                  </div>

                  <Typography
                    as="h1"
                    size="2xl"
                    className="text-secondary-700 lg:text-3xl dark:text-white"
                  >
                    {getStoryNameInLocale(story, lang)}
                  </Typography>
                </div>

                {story.content.introduction && (
                  <Typography
                    size="lg"
                    className="mx-auto mb-6 max-w-2xl leading-[150%] text-tertiary-900 lg:text-xl dark:text-tertiary-100"
                  >
                    {story.content.introduction}
                  </Typography>
                )}

                <PageBody
                  blok={story.content.body}
                  contentClassName="mx-auto max-w-2xl"
                />

                {/* Show the connected brands and categories */}
                {(Array.isArray(story.content.brand) ||
                  Array.isArray(story.content.categories)) && (
                  <ul className="mx-auto mt-7 flex max-w-2xl flex-wrap gap-2 lg:mt-14">
                    {[
                      ...(story.content.brand || []).map((item) => ({
                        ...item,
                        filterType: "brand" as const,
                      })),
                      ...(story.content.categories || []).map((item) => ({
                        ...item,
                        filterType: "category" as const,
                      })),
                    ].map(
                      (
                        item: {
                          uuid: string;
                          name: string;
                          filterType: "brand" | "category";
                        },
                        index
                      ) => (
                        <li key={`${item.filterType}-${item.uuid}-${index}`}>
                          <Button
                            size="md"
                            variant="secondary"
                            id={`filter-button-${item.filterType}-${item.uuid}`}
                            className="rounded-lg border border-tertiary-200"
                            renderProp={
                              <Link
                                to={{
                                  pathname:
                                    currentLang === languageDutch
                                      ? "/"
                                      : `/${currentLang}`,
                                  search: `?${item.filterType}=${item.uuid}`,
                                }}
                              />
                            }
                          >
                            {item.name}
                          </Button>
                        </li>
                      )
                    )}
                  </ul>
                )}

                <div className="mx-auto mt-14 max-w-2xl">
                  <InteractButtons
                    t={t}
                    onLikeClick={() => toggleLike({ articleId: story.id })}
                    liked={likeData?.liked}
                    likeCount={likeData?.likeCount ?? 0}
                    id="like-bottom"
                  />
                </div>

                <div className="mx-auto max-w-2xl text-lg">
                  <Button
                    size="xl"
                    variant="primary"
                    id="news-overview-button"
                    className="mt-6 lg:mt-14"
                    startIcon="ChevronLeft"
                    renderProp={
                      <Link
                        to={
                          currentLang === languageDutch
                            ? {
                                pathname: "/",
                                search: preservedSearch || undefined,
                              }
                            : {
                                pathname: `/${currentLang}`,
                                search: preservedSearch || undefined,
                              }
                        }
                        state={linkState}
                      />
                    }
                  >
                    {t("NewsDetailPage.newsOverview")}
                  </Button>
                </div>

                {hasRelatedStories && (
                  <nav
                    className="mx-auto mt-14 flex max-w-2xl flex-col gap-2 lg:mt-16 lg:gap-4"
                    aria-labelledby="recent-articles"
                  >
                    <Typography
                      size="lg"
                      className="text-secondary-700 dark:text-white"
                      id="recent-articles"
                    >
                      {t("NewsDetailPage.recentArticles")}
                    </Typography>

                    <ul className="flex flex-col gap-2">
                      {relatedStories?.map((relatedStory) => (
                        <li key={relatedStory.uuid}>
                          <NewsListItem
                            title={getStoryNameInLocale(relatedStory, lang)}
                            slug={
                              currentLang === languageDutch
                                ? `/news/${relatedStory.slug}`
                                : `/${lang}/news/${relatedStory.slug}`
                            }
                            imageUrl={
                              relatedStory.content.featuredImage.filename
                            }
                            imageAlt={
                              relatedStory.content.featuredImage.alt ?? ""
                            }
                            date={relatedStory.content.date}
                            cluster={relatedStory.content.cluster?.[0]?.name}
                            linkState={linkState}
                          />
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
              </div>
            </div>
          </article>
        </>
      )}
    </Layout>
  );
};

interface InteractButtonsProps {
  t: (key: string) => string;
  onLikeClick: () => void;
  liked: boolean | undefined;
  likeCount: number;
  id: string;
}

const InteractButtons = ({
  t,
  onLikeClick,
  liked,
  likeCount,
  id,
}: InteractButtonsProps) => {
  const largeButtonClasses =
    "lg:h-8 lg:px-3.5 lg:text-base lg:[&>svg]:h-5 lg:[&>svg]:w-5";

  return (
    <div className="flex items-center gap-2">
      <Button
        id={id}
        variant="secondary"
        size="md"
        startIcon="ThumbsUp"
        className={twMerge(
          "cursor-pointer gap-1",
          liked
            ? "[&>svg]:fill-accent_two-100 [&>svg]:stroke-accent_two-500"
            : "",
          largeButtonClasses
        )}
        onClick={onLikeClick}
        aria-pressed={liked}
        aria-label={t("NewsDetailPage.likeButton")}
      >
        {likeCount}
      </Button>
      <ShareButton
        text={t("Share.shareButton")}
        className={largeButtonClasses}
      />
    </div>
  );
};
